import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiFetch } from "../lib/apiFetch";

// ─────────────────────────────────────────────────────────────────────────
// Communications Board — every customer communication across Quo (voice + text),
// the office mailboxes the server reads (listed under "By mailbox"), and Jobber's own
// automated sends, in one place.
//
// Two totals are always shown side by side, on purpose:
//   • HANDLED        every communication, Jobber's automated mail included
//   • CONVERSATIONS  two-way human exchange only
// Conflating those is exactly how the old "over 550 a week" figure went wrong —
// ~29% of it was Jobber mailing customers, which has no inbound leg and is never
// anyone's first contact.
//
// Anything whose counterparty is unclassified is EXCLUDED from the client totals
// and surfaced in the amber banner instead. A board that silently counts crew
// texts as customer conversations is worse than no board.
// ─────────────────────────────────────────────────────────────────────────

const API = "/api/comms-board";

const fmt = (n, d = 0) =>
  (Number(n) || 0).toLocaleString("en-CA", { minimumFractionDigits: d, maximumFractionDigits: d });

const CHANNEL_LABELS = {
  quo_voice: "Phone calls (Quo)",
  quo_sms: "Texts (Quo + cell)",
  email: "Email",
  jobber_send: "Jobber automated",
};

const CHANNEL_COLORS = {
  quo_voice: "#a5ce3a",
  quo_sms: "#38bdf8",
  email: "#f59e0b",
  jobber_send: "#818cf8",
};

const KIND_LABELS = {
  client: "Clients", crew: "Crew / internal", supplier: "Suppliers",
  recruiting: "Recruiting", utility: "Utility / regulator", spam: "Spam",
  marketing: "Marketing / SaaS", other: "Other", unknown: "Unclassified",
};

function ymd(d) { return d.toISOString().slice(0, 10); }
function shift(date, n) { const d = new Date(`${date}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return ymd(d); }
function mondayOf(date) { const d = new Date(`${date}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); return ymd(d); }

// Today in the company's own time zone, worked out each time it is needed. It used to
// be the UTC date, fixed when the app was opened: from 5 pm Pacific the buttons were a
// day ahead (on a Sunday evening "Last 8 weeks" had already rolled to next week), and
// a tab left open overnight kept yesterday's dates.
function todayPacific() {
  const s = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Vancouver", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : ymd(new Date());
}

const PRESETS = [
  { key: "8w", label: "Last 8 weeks", range: (t) => [shift(mondayOf(t), -56), shift(mondayOf(t), -1)] },
  { key: "thisweek", label: "This week", range: (t) => [mondayOf(t), t] },
  { key: "thismonth", label: "This month", range: (t) => [`${t.slice(0, 7)}-01`, t] },
  { key: "90d", label: "Last 90 days", range: (t) => [shift(t, -89), t] },
];

// "Mon, Oct 12" for a stored day, and "Tue, Oct 13, 2:51 a.m." (Pacific) for an instant.
const dayLabel = (d) => (d
  ? new Date(`${d}T12:00:00Z`).toLocaleDateString("en-CA", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" })
  : "");
const stampLabel = (iso) => (iso
  ? new Date(iso).toLocaleString("en-CA", { timeZone: "America/Vancouver", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  : "never");

export default function CommsBoard() {
  const init = PRESETS[0].range(todayPacific());
  const [from, setFrom] = useState(init[0]);
  const [to, setTo] = useState(init[1]);
  const [preset, setPreset] = useState("8w");
  const [granularity, setGranularity] = useState("week");
  const [kind, setKind] = useState("client");
  const [channel, setChannel] = useState("all");
  const [direction, setDirection] = useState("all");
  const [firstOnly, setFirstOnly] = useState(false);
  const [convOnly, setConvOnly] = useState(false);

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [dir, setDir] = useState(null);
  const [showDir, setShowDir] = useState(false);
  const [dirBusy, setDirBusy] = useState(false);
  const [dirError, setDirError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // Only the newest request may paint. "Everyone" is three times the rows of "Clients
  // only", so switching quickly let the slower, older answer land last and the cards
  // showed one filter while the controls showed another.
  const reqSeq = useRef(0);

  // The triage panel opens near the top of the page; bring it into view when it is
  // opened from the button at the bottom.
  const dirRef = useRef(null);
  useEffect(() => {
    if (showDir && dirRef.current && dirRef.current.scrollIntoView) {
      dirRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [showDir]);

  const load = useCallback(async () => {
    // A date box being typed into passes through years like 0202; wait for a real one.
    if (!/^20\d{2}-\d{2}-\d{2}$/.test(from) || !/^20\d{2}-\d{2}-\d{2}$/.test(to)) return;
    const seq = ++reqSeq.current;
    setBusy(true); setError("");
    const qs = new URLSearchParams({ from, to, granularity, kind });
    if (channel !== "all") qs.set("channel", channel);
    if (direction !== "all") qs.set("direction", direction);
    if (firstOnly) qs.set("first_contact", "1");
    if (convOnly) qs.set("conversational", "1");
    try {
      const res = await apiFetch(`${API}?${qs.toString()}`);
      const j = await res.json();
      if (seq !== reqSeq.current) return;
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setData(j);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(e.message || String(e));
      setData(null);
    } finally {
      if (seq === reqSeq.current) setBusy(false);
    }
  }, [from, to, granularity, kind, channel, direction, firstOnly, convOnly]);

  useEffect(() => { load(); }, [load]);

  // Refresh and classify finish long after they were started; they must reload with
  // the filters on screen NOW, not the ones from the render they were clicked in.
  const loadRef = useRef(load);
  loadRef.current = load;

  const loadDirectory = useCallback(async () => {
    setDirBusy(true); setDirError("");
    try {
      const res = await apiFetch(`${API}?action=directory`);
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setDir(j);
    } catch (e) {
      setDirError(e.message || String(e));
    } finally { setDirBusy(false); }
  }, []);

  async function classify(partyKey, newKind) {
    setDirBusy(true);
    try {
      const res = await apiFetch(`${API}?action=classify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ party_key: partyKey, kind: newKind }),
      });
      if (res.ok) { await loadDirectory(); await loadRef.current(); }
      else setDirError((await res.json().catch(() => ({}))).error || "Classify failed");
    } catch (e) {
      setDirError(e.message || String(e));
    } finally { setDirBusy(false); }
  }

  // Run the legs as separate calls. Email is thousands of Gmail round-trips across
  // every mailbox and will not finish inside one function invocation alongside the
  // rest — that combination is what killed the very first production refresh.
  async function refresh() {
    setRefreshing(true); setError("");
    const gaps = [];
    try {
      for (const leg of ["quo,jobber", "email"]) {
        const days = leg === "email" ? 30 : 120;
        const res = await apiFetch(`${API}?action=refresh&days=${days}&legs=${leg}`, { method: "GET" });
        const j = await res.json();
        if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
        gaps.push(...(j.notes || []).filter((n) => n.startsWith("GAP ")));
      }
      await loadRef.current();
      if (gaps.length) setError(`Refreshed, with gaps — ${gaps.join(" · ")}`);
    } catch (e) {
      setError(e.message || String(e));
    } finally { setRefreshing(false); }
  }

  function applyPreset(p) {
    const [a, b] = p.range(todayPacific());
    setPreset(p.key); setFrom(a); setTo(b);
  }

  const t = data?.totals;
  const est = data?.estimate || null;
  const fresh = data?.freshness || null;
  // Under seven loaded days there is no honest weekly rate, so the page shows counts.
  const weekly = data ? data.window?.weekly_rate !== false : true;
  // A short window that is already over is not "so far".
  const countLabel = data && fresh?.complete_through && data.window?.to <= fresh.complete_through ? "in this window" : "so far";
  const mailboxCount = Object.keys(data?.breakdown?.by_mailbox || {}).length;
  const seriesMax = useMemo(
    () => Math.max(1, ...((data?.series || []).map((s) => s.total))),
    [data]
  );

  return (
    <div style={S.page}>
      <h1 style={S.title}>Communications</h1>
      <div style={S.subtitle}>
        Every customer communication across Quo calls and texts, the office mailboxes
        {mailboxCount > 0 ? ` (${mailboxCount}, listed under By mailbox)` : ""}, and Jobber's
        automated sends. Counterparties are classified from a correctable directory — nothing
        unclassified is counted as a customer.
      </div>

      {/* ── how fresh is this ─────────────────────────────────────────── */}
      {fresh && (
        <div style={S.freshLine}>
          Calls and texts pulled {stampLabel(fresh.legs?.quo?.last_ok_at)} · Jobber sends{" "}
          {stampLabel(fresh.legs?.jobber?.last_ok_at)} · email {stampLabel(fresh.legs?.email?.last_ok_at).replace(/\.$/, "")}.
          {fresh.complete_through && <> Complete through {dayLabel(fresh.complete_through)}; later days fill in overnight.</>}
          {fresh.mirror_through && fresh.complete_through && fresh.mirror_through < fresh.complete_through && (
            <> Texts copied from the owner's phone are in up to {dayLabel(fresh.mirror_through)}, so text counts after that are low until the phone next syncs.</>
          )}
          {busy && data && <span style={S.updating}> Updating…</span>}
        </div>
      )}

      {fresh?.problems?.length > 0 && (
        <div style={S.errorBanner}>
          <span>
            <strong>The data may be incomplete.</strong> {fresh.problems.join(" ")}
          </span>
        </div>
      )}

      {error && (
        <div style={S.errorBanner}>
          <span>{error}</span>
          <button style={S.bannerClose} onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* ── controls ─────────────────────────────────────────────────── */}
      <div style={S.controls}>
        <div style={S.segmented}>
          {PRESETS.map((p) => (
            <button key={p.key} style={preset === p.key ? S.segActive : S.seg} onClick={() => applyPreset(p)}>
              {p.label}
            </button>
          ))}
        </div>

        <div style={S.dateGroup}>
          <input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} style={S.dateInput} />
          <span style={S.dateSep}>→</span>
          <input type="date" value={to} min={from} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} style={S.dateInput} />
        </div>

        <div style={S.segmented}>
          {["day", "week", "month"].map((g) => (
            <button key={g} style={granularity === g ? S.segActive : S.seg} onClick={() => setGranularity(g)}>
              {g === "day" ? "Daily" : g === "week" ? "Weekly" : "Monthly"}
            </button>
          ))}
        </div>

        <button style={S.refreshBtn} onClick={refresh} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "↻ Refresh data"}
        </button>
      </div>

      <div style={S.controls}>
        <label style={S.filterLabel}>
          Who
          <select value={kind} onChange={(e) => setKind(e.target.value)} style={S.select}>
            <option value="client">Clients only</option>
            <option value="all">Everyone (raw)</option>
            {Object.entries(KIND_LABELS).filter(([k]) => k !== "client").map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>

        <label style={S.filterLabel}>
          Channel
          <select value={channel} onChange={(e) => setChannel(e.target.value)} style={S.select}>
            <option value="all">All channels</option>
            {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>

        <label style={S.filterLabel}>
          Direction
          <select value={direction} onChange={(e) => setDirection(e.target.value)} style={S.select}>
            <option value="all">Both ways</option>
            <option value="inbound">Incoming only</option>
            <option value="outbound">Outgoing only</option>
          </select>
        </label>

        <label style={S.check}>
          <input type="checkbox" checked={firstOnly} onChange={(e) => setFirstOnly(e.target.checked)} />
          First contact only
        </label>

        <label style={S.check}>
          <input type="checkbox" checked={convOnly} onChange={(e) => setConvOnly(e.target.checked)} />
          Two-way conversations only
        </label>
      </div>

      {/* ── coverage warning ─────────────────────────────────────────── */}
      {data?.coverage?.warning && (
        <div style={S.warnBanner}>
          <span>
            ⚠️ {data.coverage.warning}{" "}
            {data.coverage.unclassified_share_pct !== null && (
              <><strong>{fmt(data.coverage.unclassified_share_pct, 1)}%</strong> of this view is unclassified.</>
            )}
          </span>
          <button style={S.warnBtn} onClick={() => { setShowDir(true); loadDirectory(); }}>Triage →</button>
        </div>
      )}

      {/* ── directory triage — directly under the button that opens it ── */}
      {showDir && (
        <div style={S.card} ref={dirRef}>
          <div style={S.cardTitleRow}>
            <div style={S.cardTitle}>Directory — who is this?</div>
            <button style={S.todayBtn} onClick={() => setShowDir(false)}>Close</button>
          </div>
          {dirError && <div style={S.dirError}>{dirError}</div>}
          <div style={S.statSub}>
            {dir
              ? `${fmt(dir.classified)} classified · ${fmt(dir.unknown_parties)} unknown counterparties behind ${fmt(dir.unknown_communications)} communications`
              : dirError ? "The directory could not be loaded." : "Loading…"}
            {dir && dir.unknown_no_key > 0 && ` · ${fmt(dir.unknown_no_key)} more have no caller number or recipient, so there is nothing to classify`}
          </div>
          {dir?.queue?.map((q) => (
            <div key={q.party_key} style={S.dirRow}>
              <div style={S.dirKey}>{q.party_key}</div>
              <div style={S.dirMeta}>
                {fmt(q.count)} comms · {fmt(q.inbound)} in · {q.channels.join(", ")} · {q.first} → {q.last}
              </div>
              <div style={S.dirActions}>
                {(dir.kinds || []).map((k) => (
                  <button key={k} style={S.dirBtn} disabled={dirBusy} onClick={() => classify(q.party_key, k)} title={`Mark as ${k}`}>
                    {k}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {dir && dir.queue.length === 0 && <div style={S.emptyInline}>Nothing left to triage.</div>}
        </div>
      )}

      {busy && !data && <div style={S.empty}>Loading…</div>}

      {t && (
        <>
          {/* ── the two headline numbers ──────────────────────────────── */}
          <div style={S.headlineRow}>
            <div style={S.headlineCard}>
              <div style={S.statLabel}>Two-way conversations</div>
              <div style={S.headlineValue}>
                {weekly ? fmt(t.conversational_per_week, 1) : fmt(t.conversational)}
                {est && weekly && est.conversations_upper_per_week > t.conversational_per_week && (
                  <span style={S.headlineRange}>–{fmt(est.conversations_upper_per_week, 1)}</span>
                )}
                {est && !weekly && est.conversations_upper > t.conversational && (
                  <span style={S.headlineRange}>–{fmt(est.conversations_upper)}</span>
                )}
                <span style={S.perWeek}>{weekly ? "/week" : countLabel}</span>
              </div>
              <div style={S.statSub}>
                {fmt(t.conversational)} in window · calls, texts and emails with a human on both ends
                {est && est.unclassified_two_way_conversational > 0 && (
                  <> · upper adds {fmt(est.unclassified_two_way_conversational)} from {fmt(est.unclassified_two_way_parties)} unclassified counterparties we both wrote to <em>and</em> heard from</>
                )}
              </div>
            </div>
            <div style={S.headlineCardMuted}>
              <div style={S.statLabel}>Total handled</div>
              <div style={S.headlineValueMuted}>
                {weekly ? fmt(t.per_week, 1) : fmt(t.total)}
                {est && weekly && est.total_upper_per_week > t.per_week && (
                  <span style={S.headlineRangeMuted}>–{fmt(est.total_upper_per_week, 1)}</span>
                )}
                {est && !weekly && est.total_upper > t.total && (
                  <span style={S.headlineRangeMuted}>–{fmt(est.total_upper)}</span>
                )}
                <span style={S.perWeek}>{weekly ? "/week" : countLabel}</span>
              </div>
              <div style={S.statSub}>
                {fmt(t.total)} in window · includes {fmt(t.automated)} automated Jobber sends
                {t.total > 0 && ` (${fmt((100 * t.automated) / t.total, 0)}%)`}
                {est && est.unclassified_two_way_rows > 0 && (
                  <> · upper adds {fmt(est.unclassified_two_way_rows)} unclassified two-way</>
                )}
              </div>
            </div>
          </div>

          {/* ── stat tiles ────────────────────────────────────────────── */}
          <div style={S.statGrid}>
            <Stat
              label="Incoming"
              value={fmt(t.inbound)}
              sub={weekly ? `${fmt(t.inbound_per_week, 1)}/week — customer reached out` : "customer reached out"}
            />
            <Stat label="Outgoing" value={fmt(t.outbound)} sub={`${fmt(t.outbound - t.automated)} human + ${fmt(t.automated)} automated`} />
            <Stat
              label="Follow-up share"
              value={t.follow_up_share === null ? "—" : `${fmt(t.follow_up_share, 1)}%`}
              sub={`${fmt(t.follow_up)} follow-ups vs ${fmt(t.first_contact)} first contacts`}
              accent
            />
            <Stat
              label="Distinct counterparties"
              value={fmt(t.distinct_parties)}
              sub="in this window — phone/email/Jobber keys, not resolved identities"
            />
            <Stat
              label="AI answered"
              value={data.ai_receptionist.share_pct === null ? "—" : `${fmt(data.ai_receptionist.share_pct, 1)}%`}
              sub={`${fmt(data.ai_receptionist.answered)} of ${fmt(data.ai_receptionist.eligible)} inbound calls`}
            />
            <Stat label="Window" value={`${fmt(data.window.weeks, 1)} wks`} sub={`${data.window.from} → ${data.window.to}`} />
          </div>

          <div style={S.note}>
            {!weekly && (
              <>There is under a week of loaded days in this window, so it shows counts, not a weekly rate. </>
            )}
            {weekly && data.window.rate_through && data.window.rate_through < data.window.to && (
              <>Weekly rates are worked out over the days up to {dayLabel(data.window.rate_through)}; later days are not fully loaded yet and are left out of the rates. </>
            )}
            {(data.coverage?.history_starts || []).map((h) => (
              <span key={h.channel}>
                {CHANNEL_LABELS[h.channel] || h.channel} is only loaded from {dayLabel(h.first_day)}, so this
                window is short of it at the start and its weekly figures read low.{" "}
              </span>
            ))}
            {data.ai_receptionist.note} Follow-up share is a floor — the call log only reaches back to
            2026-06-27, so first contacts are over-counted and follow-ups under-counted. "Distinct
            counterparties" counts keys, not people: one customer reached by phone, email and a Jobber
            send is three keys, so never quote it as a customer count. Jobber's automated sends are
            dated by the week their quote or invoice was created, not the day they went out — the API
            exposes no send timestamp — and that leg is a floor, because roughly 31% of Jobber's client
            mail isn't linked to a quote, invoice or assessment at all. When one email reaches two of
            our mailboxes the refresh keeps a single copy, so "By mailbox" shows where a message was
            first read, not everyone who received it.
          </div>

          {/* ── series ────────────────────────────────────────────────── */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              {granularity === "day" ? "By day" : granularity === "week" ? "By week" : "By month"}
            </div>
            {data.series.length === 0 && <div style={S.emptyInline}>No communications in this window.</div>}
            {data.series.map((s) => (
              <div key={s.bucket} style={S.barRow}>
                <div style={S.barLabel}>{s.bucket}</div>
                <div style={S.barTrack}>
                  <div style={{ ...S.barFill, width: `${(s.conversational / seriesMax) * 100}%` }} />
                  <div style={{ ...S.barFillAuto, width: `${((s.total - s.conversational) / seriesMax) * 100}%` }} />
                </div>
                <div style={S.barValue}>{fmt(s.total)}</div>
                <div style={S.barSplit}>↓{fmt(s.inbound)} ↑{fmt(s.outbound)}</div>
              </div>
            ))}
            <div style={S.legend}>
              <span style={S.legendItem}><i style={{ ...S.dot, background: "#a5ce3a" }} /> two-way conversation</span>
              <span style={S.legendItem}><i style={{ ...S.dot, background: "#3f4652" }} /> automated / one-way</span>
            </div>
          </div>

          {/* ── breakdowns ────────────────────────────────────────────── */}
          <div style={S.grid2}>
            <Breakdown title="By channel" rows={data.breakdown.by_channel} labels={CHANNEL_LABELS} colors={CHANNEL_COLORS} />
            <Breakdown title="By Quo line" rows={data.breakdown.by_line} empty="No line tagging on these rows — texts carry no line number." />
            <Breakdown title="By mailbox" rows={data.breakdown.by_mailbox} empty="No email under this filter." />
            <Breakdown title="Who we talked to" rows={data.breakdown.by_kind} labels={KIND_LABELS} />
          </div>
        </>
      )}

      {!showDir && (
        <button style={S.todayBtn} onClick={() => { setShowDir(true); loadDirectory(); }}>
          Open directory triage
        </button>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div style={S.stat}>
      <div style={S.statLabel}>{label}</div>
      <div style={accent ? S.statValueAccent : S.statValue}>{value}</div>
      {sub && <div style={S.statSub}>{sub}</div>}
    </div>
  );
}

function Breakdown({ title, rows, labels = {}, colors = {}, empty }) {
  const entries = Object.entries(rows || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>{title}</div>
      {entries.length === 0 && <div style={S.emptyInline}>{empty || "Nothing here."}</div>}
      {entries.map(([k, v]) => (
        <div key={k} style={S.catRow}>
          <div style={S.catLabel}>{labels[k] || k}</div>
          <div style={S.catBar}>
            <div style={{ ...S.catBarFill, width: `${(v / max) * 100}%`, background: colors[k] || "#94a3b8" }} />
          </div>
          <div style={S.catValue}>{fmt(v)}</div>
        </div>
      ))}
    </div>
  );
}

const S = {
  page: { padding: 24, color: "#e8eaf0", fontFamily: "'Roboto', sans-serif", maxWidth: 1400, margin: "0 auto" },
  title: { fontSize: 26, fontWeight: 800, margin: 0 },
  subtitle: { fontSize: 13, color: "#8892a4", marginTop: 4, maxWidth: 760, lineHeight: 1.5 },
  freshLine: { fontSize: 12, color: "#8892a4", marginTop: 8, maxWidth: 900, lineHeight: 1.5 },
  updating: { color: "#a5ce3a", fontWeight: 600 },
  dirError: { color: "#fca5a5", fontSize: 12, margin: "4px 0 8px" },

  errorBanner: { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", padding: "10px 14px", borderRadius: 8, margin: "12px 0", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  bannerClose: { background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 },
  warnBanner: { background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.35)", color: "#fcd34d", padding: "10px 14px", borderRadius: 8, margin: "12px 0", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  warnBtn: { background: "#f59e0b", border: "none", color: "#0d0f12", fontWeight: 700, fontSize: 12, padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },

  controls: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 16 },
  segmented: { display: "inline-flex", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, overflow: "hidden" },
  seg: { padding: "8px 14px", background: "transparent", border: "none", color: "#8892a4", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  segActive: { padding: "8px 14px", background: "#a5ce3a", border: "none", color: "#0d0f12", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  dateGroup: { display: "inline-flex", alignItems: "center", gap: 8 },
  dateInput: { background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, padding: "7px 10px", fontFamily: "inherit" },
  dateSep: { color: "#8892a4", fontSize: 13 },
  refreshBtn: { padding: "8px 14px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#a5ce3a", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  filterLabel: { display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.05em" },
  select: { background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, padding: "7px 10px", fontFamily: "inherit", textTransform: "none", letterSpacing: 0 },
  check: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#8892a4", cursor: "pointer", marginTop: 14 },

  headlineRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, marginTop: 20 },
  headlineCard: { background: "#161a20", border: "1px solid rgba(165,206,58,0.35)", borderRadius: 12, padding: 18 },
  headlineCardMuted: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 18 },
  headlineValue: { fontSize: 38, fontWeight: 800, color: "#a5ce3a", lineHeight: 1.1 },
  headlineValueMuted: { fontSize: 38, fontWeight: 800, color: "#e8eaf0", lineHeight: 1.1 },
  perWeek: { fontSize: 15, fontWeight: 600, color: "#8892a4", marginLeft: 6 },
  headlineRange: { fontSize: 26, fontWeight: 700, color: "#a5ce3a", opacity: 0.65 },
  headlineRangeMuted: { fontSize: 26, fontWeight: 700, color: "#e8eaf0", opacity: 0.5 },

  statGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginTop: 14 },
  stat: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 14 },
  statLabel: { fontSize: 11, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 800, color: "#f0f0f0" },
  statValueAccent: { fontSize: 22, fontWeight: 800, color: "#a5ce3a" },
  statSub: { fontSize: 11, color: "#8892a4", marginTop: 4, lineHeight: 1.45 },
  note: { fontSize: 12, color: "#8892a4", marginTop: 12, lineHeight: 1.5, maxWidth: 900 },

  card: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 18, marginTop: 16 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#e8eaf0", marginBottom: 10 },
  cardTitleRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 16 },

  barRow: { display: "grid", gridTemplateColumns: "92px 1fr 52px 72px", alignItems: "center", gap: 10, padding: "3px 0" },
  barLabel: { fontSize: 11, color: "#8892a4", fontVariantNumeric: "tabular-nums" },
  barTrack: { display: "flex", height: 16, background: "#0f1216", borderRadius: 4, overflow: "hidden" },
  barFill: { background: "#a5ce3a", height: "100%" },
  barFillAuto: { background: "#3f4652", height: "100%" },
  barValue: { fontSize: 12, fontWeight: 700, color: "#e8eaf0", textAlign: "right", fontVariantNumeric: "tabular-nums" },
  barSplit: { fontSize: 11, color: "#8892a4", textAlign: "right", fontVariantNumeric: "tabular-nums" },
  legend: { display: "flex", gap: 16, marginTop: 10, fontSize: 11, color: "#8892a4" },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 6 },
  dot: { width: 9, height: 9, borderRadius: 2, display: "inline-block" },

  catRow: { display: "grid", gridTemplateColumns: "1fr 110px 46px", alignItems: "center", gap: 10, padding: "4px 0" },
  catLabel: { fontSize: 12, color: "#c9cfdb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  catBar: { height: 8, background: "#0f1216", borderRadius: 4, overflow: "hidden" },
  catBarFill: { height: "100%", borderRadius: 4 },
  catValue: { fontSize: 12, fontWeight: 700, color: "#e8eaf0", textAlign: "right", fontVariantNumeric: "tabular-nums" },

  dirRow: { display: "grid", gridTemplateColumns: "150px 1fr auto", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.06)" },
  dirKey: { fontSize: 13, fontWeight: 700, color: "#e8eaf0", fontVariantNumeric: "tabular-nums" },
  dirMeta: { fontSize: 11, color: "#8892a4" },
  dirActions: { display: "flex", gap: 4, flexWrap: "wrap" },
  dirBtn: { background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 5, color: "#c9cfdb", fontSize: 11, padding: "4px 8px", cursor: "pointer", fontFamily: "inherit" },

  todayBtn: { padding: "7px 12px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#8892a4", fontSize: 12, cursor: "pointer", fontFamily: "inherit", marginTop: 16 },
  empty: { padding: 40, textAlign: "center", color: "#8892a4" },
  emptyInline: { padding: "10px 2px", color: "#8892a4", fontSize: 12, lineHeight: 1.5 },
};
